import type { MouseEvent } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useImpressionRef } from '@noths/polaris-client-react-hooks';
import { Icon } from '@noths/polaris-design-system-core-components';
import useEmblaCarousel from 'embla-carousel-react';

import type { HeroCarouselCaptionProps } from 'src/components/molecules/HeroCarouselCaption/HeroCarouselCaption';
import { HeroCarouselCaption } from 'src/components/molecules/HeroCarouselCaption/HeroCarouselCaption';
import type { HeroCarouselItemProps } from 'src/components/molecules/HeroCarouselItem/HeroCarouselItem';
import { HeroCarouselItem } from 'src/components/molecules/HeroCarouselItem/HeroCarouselItem';
import { CountdownRing } from './CountdownRing';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';
import * as styles from './HeroCarousel.styles';

/** A full hero slide carries both the sliding media props and the swapping caption props. */
export type HeroCarouselSlideProps = HeroCarouselItemProps & HeroCarouselCaptionProps;

export interface HeroCarouselProps {
  autoplay?: boolean;
  autoplayInterval?: number;
  items: HeroCarouselSlideProps[];
  onItemButtonClick?: (e: MouseEvent<HTMLAnchorElement>, index: number) => void;
  onItemImageClick?: (e: MouseEvent<HTMLAnchorElement>, index: number) => void;
  /** `swiped` is true only for pointer-drag navigation; false for arrow buttons, arrow keys, and autoplay. */
  onSlideChange?: (index: number, swiped: boolean) => void;
  onVisible?: () => void;
}

const HeroCarouselTrack = ({
  autoplay = true,
  autoplayInterval = 5000,
  items,
  onItemButtonClick,
  onItemImageClick,
  onSlideChange,
}: Omit<HeroCarouselProps, 'onVisible'>) => {
  const reducedMotion = usePrefersReducedMotion();
  const autoplayEnabled = autoplay && !reducedMotion;
  const swipedRef = useRef(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  // Reserve the terms line on every slide if any slide has T&Cs, so the height stays steady as
  // slides change (see HeroCarouselCaption).
  const anyItemHasTerms = items.some((item) => Boolean(item.termsAndConditions));
  // The countdown ring is the autoplay timer (see CountdownRing): pausing freezes it in place and
  // resumes from the same point, so hovering holds the countdown rather than restarting it.
  const [paused, setPaused] = useState(false);

  const [emblaRef, emblaApi] = useEmblaCarousel({ align: 'center', loop: true });

  const scrollPrevious = useCallback(() => {
    emblaApi?.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    emblaApi?.scrollNext();
  }, [emblaApi]);

  const pauseAutoplay = useCallback(() => setPaused(true), []);
  const resumeAutoplay = useCallback(() => setPaused(false), []);

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowLeft') {
      scrollPrevious();
    } else if (event.key === 'ArrowRight') {
      scrollNext();
    }
  };

  useEffect(() => {
    if (!emblaApi) {
      return undefined;
    }

    const handleSelect = () => {
      const index = emblaApi.selectedScrollSnap();
      setSelectedIndex(index);
      onSlideChange?.(index, swipedRef.current);
      swipedRef.current = false;
    };

    const handlePointerDown = () => {
      swipedRef.current = true;
    };

    emblaApi.on('select', handleSelect);
    emblaApi.on('pointerDown', handlePointerDown);

    return () => {
      emblaApi.off('select', handleSelect);
      emblaApi.off('pointerDown', handlePointerDown);
    };
  }, [emblaApi, onSlideChange]);

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- delegates ArrowLeft/ArrowRight from focusable children (arrow buttons / CTA links) to drive carousel navigation
    <section
      aria-label="Hero carousel"
      aria-roledescription="carousel"
      css={styles.carousel}
      data-autoplay={autoplayEnabled}
      onKeyDown={onKeyDown}
    >
      <div css={styles.viewport} ref={emblaRef}>
        <div css={styles.container}>
          {items.map((item, index) => (
            <div
              aria-label={`slide ${index + 1} of ${items.length}`}
              aria-roledescription="slide"
              css={styles.slide}
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              // Pause while the pointer is over the slide currently in view (the arrows pause too,
              // below); leaving it for the caption or a peeking neighbour resumes the timer.
              onMouseEnter={index === selectedIndex ? pauseAutoplay : undefined}
              onMouseLeave={index === selectedIndex ? resumeAutoplay : undefined}
              role="group"
            >
              <HeroCarouselItem
                alt={item.alt}
                headingLevel={index === selectedIndex ? 'h1' : 'h2'}
                headingLine1={item.headingLine1}
                headingLine2={item.headingLine2}
                headingPosition={item.headingPosition}
                // Only the active slide's image links, so tapping a peeking neighbour can't navigate.
                href={index === selectedIndex ? item.buttonHref : undefined}
                image={item.image}
                imageMobile={item.imageMobile}
                onImageClick={(e) => onItemImageClick?.(e, index)}
                priority={index === 0}
              />
            </div>
          ))}
        </div>
        <button
          aria-label="Previous slide"
          css={[styles.control, styles.controlPrevious]}
          onClick={scrollPrevious}
          onMouseEnter={pauseAutoplay}
          onMouseLeave={resumeAutoplay}
          type="button"
        >
          <Icon name="actionBack" size="lg" />
        </button>
        <button
          aria-label="Next slide"
          css={[styles.control, styles.controlNext]}
          onClick={scrollNext}
          onMouseEnter={pauseAutoplay}
          onMouseLeave={resumeAutoplay}
          type="button"
        >
          <Icon name="actionForward" size="lg" />
          {autoplayEnabled && (
            <CountdownRing
              cycleKey={selectedIndex}
              interval={autoplayInterval}
              onComplete={scrollNext}
              paused={paused}
            />
          )}
        </button>
      </div>

      {/* Single, non-sliding caption for the active slide; re-mounting on index change cross-fades the new text in. */}
      <HeroCarouselCaption
        buttonHref={items[selectedIndex].buttonHref}
        buttonText={items[selectedIndex].buttonText}
        key={selectedIndex}
        onButtonClick={(e) => onItemButtonClick?.(e, selectedIndex)}
        paragraph={items[selectedIndex].paragraph}
        reserveTermsSpace={anyItemHasTerms}
        termsAndConditions={items[selectedIndex].termsAndConditions}
      />
    </section>
  );
};

export const HeroCarousel = ({ onVisible, ...props }: HeroCarouselProps) => {
  const impressionRef = useImpressionRef<HTMLDivElement>(() => onVisible?.());
  const { items, onItemButtonClick, onItemImageClick } = props;

  if (items.length === 0) {
    return null;
  }

  return (
    <div ref={impressionRef}>
      {items.length === 1 ? (
        <div css={styles.singleItem}>
          <HeroCarouselItem
            alt={items[0].alt}
            headingLevel="h1"
            headingLine1={items[0].headingLine1}
            headingLine2={items[0].headingLine2}
            headingPosition={items[0].headingPosition}
            href={items[0].buttonHref}
            image={items[0].image}
            imageMobile={items[0].imageMobile}
            onImageClick={(e) => onItemImageClick?.(e, 0)}
            priority
          />
          <HeroCarouselCaption
            fullWidth
            {...items[0]}
            onButtonClick={(e) => onItemButtonClick?.(e, 0)}
          />
        </div>
      ) : (
        <HeroCarouselTrack {...props} />
      )}
    </div>
  );
};
